// frontend/src/pages/NewUIVerifyEmail.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from '../config/axios';

// Botón gaming reutilizable
function GamingButton({ variant = "secondary", className = "", leftIcon, children, disabled, ...props }) {
    const base =
        "inline-flex items-center justify-center gap-2.5 " +
        "h-12 md:h-14 px-6 md:px-8 " +
        "rounded-[14px] md:rounded-[16px] " +
        "font-bold tracking-wider leading-none " +
        "transition-transform duration-150 hover:scale-[1.02] active:scale-[0.98] " +
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100";

    const variants = {
        primary: "bg-accent text-secondary hover:bg-accent-light border-2 border-accent-dark",
        secondary: "bg-primary text-white hover:bg-primary-light border-2 border-primary-dark",
        ctaBlue: "bg-[#2284DF] text-white hover:bg-[#2A91F4] border-2 border-[#1E75C6]",
        ctaYellow: "bg-[#FCE503] text-[#122436] hover:bg-[#FCE503]/90 border-2 border-[#E5CF03]",
    };

    return (
        <button
            className={`${base} ${variants[variant]} ${className}`}
            disabled={disabled}
            {...props}
        >
            {leftIcon && <span className="inline-flex items-center">{leftIcon}</span>}
            <span className="whitespace-nowrap">{children}</span>
        </button>
    );
}

const NewUIVerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('verifying');
    const [error, setError] = useState(null);
    const token = searchParams.get('token');
    const verificationAttempted = useRef(false);

    // Aplicar scope de Acumin
    useEffect(() => {
        document.body.classList.add('acumin-scope');
        return () => {
            document.body.classList.remove('acumin-scope');
        };
    }, []);

    useEffect(() => {
        const verifyEmail = async () => {
            if (!token) {
                console.log('No se encontró token en la URL');
                setStatus('error');
                setError('No se proporcionó token de verificación');
                return;
            }

            // Evitar verificaciones duplicadas
            if (verificationAttempted.current) {
                console.log('Verificación ya intentada, ignorando...');
                return;
            }

            try {
                verificationAttempted.current = true;
                console.log('Iniciando verificación con token:', token);

                const response = await axios.get(`/auth/verify-email/${token}`);
                console.log('Respuesta recibida:', response.data);

                if (response.data.success) {
                    setStatus('success');
                } else {
                    setStatus('error');
                    setError(response.data.message);
                }
            } catch (error) {
                console.error('Error en la verificación:', error);
                setStatus('error');
                setError(error.response?.data?.message || 'Error al verificar el email');
            }
        };

        verifyEmail();
    }, [token]);

    return (
        <div
            className="min-h-screen flex items-center justify-center pt-24 pb-12 px-4 sm:px-6 lg:px-8"
            style={{
                backgroundImage: 'url(/images/backgrounds/hero-radial-bg.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <div className="w-full max-w-md">
                {/* Card */}
                <div
                    className="rounded-[24px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
                    style={{
                        backgroundColor: '#122436',
                        border: '2px solid rgba(255,255,255,0.55)'
                    }}
                >
                    <div className="px-6 sm:px-8 py-10">
                        <h2 className="text-2xl md:text-3xl font-extrabold text-white text-center mb-8 uppercase tracking-wider">
                            Verificación de Email
                        </h2>

                        {/* Estado: Verificando */}
                        {status === 'verifying' && (
                            <div className="text-center">
                                <div className="mb-6 flex justify-center">
                                    <div
                                        className="w-20 h-20 rounded-full flex items-center justify-center"
                                        style={{
                                            backgroundColor: 'rgba(34, 132, 223, 0.15)',
                                            border: '3px solid #2284DF'
                                        }}
                                    >
                                        <div
                                            className="w-12 h-12 border-4 border-[#2284DF] border-t-transparent rounded-full animate-spin"
                                        />
                                    </div>
                                </div>
                                <p className="text-white/80 mb-4 font-semibold">
                                    Verificando tu email...
                                </p>
                                <p className="text-sm text-white/60">
                                    Por favor esperá un momento
                                </p>
                            </div>
                        )}

                        {/* Estado: Éxito */}
                        {status === 'success' && (
                            <div className="text-center space-y-6">
                                {/* Success Icon */}
                                <div className="flex justify-center mb-4">
                                    <div
                                        className="w-20 h-20 rounded-full flex items-center justify-center"
                                        style={{
                                            backgroundColor: 'rgba(34, 223, 100, 0.15)',
                                            border: '3px solid #22DF64'
                                        }}
                                    >
                                        <svg
                                            className="w-10 h-10 text-[#22DF64]"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={3}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    </div>
                                </div>

                                <div
                                    className="rounded-lg p-4 mb-4"
                                    style={{
                                        backgroundColor: 'rgba(34, 223, 100, 0.15)',
                                        border: '2px solid #22DF64'
                                    }}
                                >
                                    <p className="text-white/90 font-bold">
                                        ¡Tu email ha sido verificado exitosamente!
                                    </p>
                                </div>

                                <p className="text-sm text-white/70">
                                    Ya podés iniciar sesión y empezar a jugar
                                </p>

                                <Link to="/login" className="block">
                                    <GamingButton
                                        variant="ctaYellow"
                                        className="w-full text-base uppercase"
                                    >
                                        Ir al Login
                                    </GamingButton>
                                </Link>
                            </div>
                        )}

                        {/* Estado: Error */}
                        {status === 'error' && (
                            <div className="text-center space-y-6">
                                {/* Error Icon */}
                                <div className="flex justify-center mb-4">
                                    <div
                                        className="w-20 h-20 rounded-full flex items-center justify-center"
                                        style={{
                                            backgroundColor: 'rgba(253, 2, 3, 0.15)',
                                            border: '3px solid #FD0203'
                                        }}
                                    >
                                        <svg
                                            className="w-10 h-10 text-[#FD0203]"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={3}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </div>
                                </div>

                                <div
                                    className="rounded-lg p-4 mb-4"
                                    style={{
                                        backgroundColor: 'rgba(253, 2, 3, 0.15)',
                                        border: '2px solid #FD0203'
                                    }}
                                >
                                    <p className="text-white/90 font-bold mb-2">
                                        Error en la verificación
                                    </p>
                                    <p className="text-sm text-white/75">
                                        {error || 'El link de verificación es inválido o ha expirado.'}
                                    </p>
                                </div>

                                <p className="text-sm text-white/70">
                                    Por favor, intentá registrarte nuevamente o contactá con soporte si el problema persiste.
                                </p>

                                <div className="space-y-3">
                                    <Link to="/register" className="block">
                                        <GamingButton
                                            variant="ctaYellow"
                                            className="w-full text-base uppercase"
                                        >
                                            Volver al Registro
                                        </GamingButton>
                                    </Link>

                                    <a
                                        href="#"
                                        className="block text-sm text-[#2284DF] hover:text-[#2A91F4] font-semibold transition-colors"
                                    >
                                        Contactar Soporte
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Back to home link */}
                <div className="mt-6 text-center">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors font-semibold"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 19l-7-7m0 0l7-7m-7 7h18"
                            />
                        </svg>
                        Volver al inicio
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default NewUIVerifyEmail;